import { createContext, useContext } from 'react';

/**
 * Whether the Firebase subscription session reflects the current Descope
 * session (PPL-3826). Firebase is EBP's Firestore/Storage subscription
 * credential; `DescopeSessionProvider`'s reconciliation supplies this so the
 * UI can hold protected content - and its live reads - until Firebase is in
 * step with the account and permissions the Descope session says are current.
 *
 * - `syncing` - reconciliation in progress (initial sign-in, a reload, an
 *   account switch, or a permission re-mint). Wait; do not render protected
 *   content against a not-yet-current session.
 * - `ready` - Firebase reflects the current Descope session.
 * - `error` - the exchange failed; `retry` re-runs it. Fail closed until then.
 */
export type FirebaseSyncStatus = 'syncing' | 'ready' | 'error';

export interface FirebaseSyncValue {
  status: FirebaseSyncStatus;
  retry: () => void;
}

export const FirebaseSyncContext = createContext<FirebaseSyncValue>({
  // Default for a tree with no reconciler above it: nothing is being kept in
  // sync, so never report a stale session as ready.
  status: 'syncing',
  retry: () => {},
});

export const useFirebaseSync = (): FirebaseSyncValue => useContext(FirebaseSyncContext);
